<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <UserAvatar v-if="adaptedUser" :user="adaptedUser" size="32px" />
            <VaAvatar v-else :size="32" color="warning"> ? </VaAvatar>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        v-if="isShown"
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <!-- 用户信息部分 -->
        <div class="px-4 py-2 border-b border-gray-100">
          <div class="flex items-center space-x-3 mb-2">
            <UserAvatar v-if="adaptedUser" :user="adaptedUser" size="48px" />
            <VaAvatar v-else :size="48" color="warning"> ? </VaAvatar>
            <div>
              <div class="font-semibold">{{ userInfo?.username || '未登录' }}</div>
              <div class="text-xs text-gray-500">{{ userInfo?.email || '' }}</div>
            </div>
          </div>
          <div v-if="userInfo?.id" class="text-xs text-gray-500 mt-1">ID: {{ userInfo.id }}</div>
        </div>

        <!-- 菜单选项部分 -->
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4 mt-2">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleMenuItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useUserStore } from '../../../../stores/user-store'
import { useAuthStore } from '../../../../stores/auth-store'
import UserAvatar from '../../../../pages/users/widgets/UserAvatar.vue'
import { User } from '../../../../pages/users/types'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()
const userStore = useUserStore()
const authStore = useAuthStore()

// 获取用户信息
const userInfo = computed(() => userStore.userInfo)

// 将UserInfo类型适配为User类型
const adaptedUser = computed(() => {
  if (!userInfo.value) return null

  // 创建一个符合User类型的对象
  return {
    id: userInfo.value.id as any, // 使用类型断言处理UUID格式要求
    username: userInfo.value.username,
    email: userInfo.value.email,
    avatar: '', // UserInfo中可能没有avatar字段
    status: userInfo.value.status,
    roleId: userInfo.value.roleId,
  } as User
})

// 存储定时器ID，以便在组件卸载前清除
let logoutTimer: number | null = null

// 在组件卸载前清除所有定时器
onBeforeUnmount(() => {
  if (logoutTimer !== null) {
    clearTimeout(logoutTimer)
    logoutTimer = null
  }
})

// 处理菜单项点击
const handleMenuItemClick = async (item: ProfileListItem) => {
  // 先关闭下拉菜单
  isShown.value = false

  // 使用nextTick确保DOM更新完成
  await nextTick()

  // 如果是登出操作
  if (item.name === 'logout') {
    // 延迟执行登出操作，确保下拉菜单已经完全关闭
    logoutTimer = window.setTimeout(() => {
      authStore.logout()
      logoutTimer = null
    }, 200) as unknown as number
  }
}

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  // 对于登出操作，不需要链接属性
  if (item.name === 'logout') return {}
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }

    max-width: 100vw;
    overflow-x: hidden;
  }

  &__anchor {
    display: inline-block;
  }
}

body {
  overflow-x: hidden;
}
</style>
